import React from 'react';
import { Flow, ProcessingResultData } from '@/types/Document';
import ProcessingResultsHeader from './ProcessingResultsHeader';
import FlowRenderer from './FlowRenderer';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import {
  LoadingSpinner,
  ProcessingErrorMessage,
  ProcessingInProgressMessage,
  NotProcessedMessage,
} from './ProcessingStatusMessages';

interface ProcessingResultsPaneProps {
  // From document object
  processingStatus: string | undefined;
  processedAt: string | undefined;
  currentLayoutType: 'stacked' | 'side-by-side';
  documentType: string;
  className?: string;
  warnings?: string[];

  // From useProcessingResults hook
  processingResults: ProcessingResultData;
  editableResults: ProcessingResultData;
  loadingResults: boolean;
  editMode: boolean;
  savingEdits: boolean;
  collapsedPhases: Set<string>;
  openMenuPath: string | null;
  setOpenMenuPath: React.Dispatch<React.SetStateAction<string | null>>;
  toggleEditMode: () => void;
  cancelEditing: () => void;
  saveEditedResults: () => void;
  handleInputChange: (
    path: (string | number)[],
    value: string,
    type?: 'flow' | 'phase' | 'step' | 'block'
  ) => void;
  togglePhaseCollapse: (flowIndex: number, phaseIndex: number) => void;
  handleDeleteItem: (itemPath: string) => void;
  handleAddItem: (parentPath: string, itemTypeToAdd: 'phase' | 'step' | 'block') => void;
  handleChangeItemType: (itemPath: string, newType: 'phase' | 'step') => void;
  handleCreateStepFromSelection: (selection: string, blockPath: string) => void;
  isFlowArray: boolean;
  hasStructuredResults: boolean;
  setEditableResultsState: (results: ProcessingResultData) => void;

  // From useSiteflowIntegration hook
  sendingToSiteflow: boolean;
  siteflowError?: string;
  siteflowMessage?: string;
  handleSendToSiteflow: (projectId: string, familyId: string) => void;
}

/**
 * Formats raw results data, parsing JSON strings if needed.
 */
function formatResultsLocal(data: unknown): Record<string, unknown> | null {
  if (!data) return null;
  if (typeof data === 'string') {
    try { 
      return JSON.parse(data); 
    } catch {
      return null;
    }
  }
  return data as Record<string, unknown>;
}

/**
 * ProcessingResultsPane - Main component for displaying document processing results.
 * Supports Flow array structure, structured objects, and raw text/JSON display.
 */
const ProcessingResultsPane: React.FC<ProcessingResultsPaneProps> = ({
  processingStatus,
  processedAt,
  currentLayoutType,
  documentType,
  className: passedClassName,
  warnings,
  processingResults,
  editableResults,
  loadingResults,
  editMode,
  savingEdits,
  collapsedPhases,
  openMenuPath,
  setOpenMenuPath,
  toggleEditMode,
  cancelEditing,
  saveEditedResults,
  handleInputChange,
  togglePhaseCollapse,
  handleDeleteItem,
  handleAddItem,
  handleChangeItemType,
  handleCreateStepFromSelection,
  isFlowArray,
  hasStructuredResults,
  setEditableResultsState,
  sendingToSiteflow,
  siteflowError,
  siteflowMessage,
  handleSendToSiteflow,
}) => {
  const resultsToDisplay = editMode ? editableResults : processingResults;
  const showExternalHeader = documentType?.toUpperCase() === 'DSI';

  // Calculate container classes
  let baseDivClasses = `overflow-auto ${currentLayoutType === 'stacked' ? 'w-full' : 'w-full md:w-1/2 h-full'}`;
  if (showExternalHeader && currentLayoutType === 'stacked') {
    baseDivClasses += ' pt-0';
  } else if (currentLayoutType === 'stacked') {
    baseDivClasses += ' pt-4';
  } else {
    baseDivClasses += ' p-4 md:pl-4';
  }
  const finalDivClasses = `${baseDivClasses} ${passedClassName || ''}`.trim();

  return (
    <div 
      className={finalDivClasses} 
      style={{ contentVisibility: 'auto' } as React.CSSProperties}
    >
      <div className={`bg-white rounded-lg shadow h-full overflow-auto flex flex-col ${showExternalHeader ? 'border-t-0' : ''}`}>
        {/* Header */}
        {!showExternalHeader && (
          <ProcessingResultsHeader 
            processingStatus={processingStatus}
            processedAt={processedAt}
            editMode={editMode}
            savingEdits={savingEdits}
            isFlowArray={isFlowArray}
            resultsToDisplay={resultsToDisplay}
            sendingToSiteflow={sendingToSiteflow}
            siteflowError={siteflowError}
            siteflowMessage={siteflowMessage}
            handleSendToSiteflow={handleSendToSiteflow}
            toggleEditMode={toggleEditMode}
            saveEditedResults={saveEditedResults}
            cancelEditing={cancelEditing}
          />
        )}

        {/* Warnings Banner */}
        {warnings && warnings.length > 0 && (
          <div className="mx-4 mt-3 mb-0 rounded-md bg-amber-50 border border-amber-300 p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-amber-800">
                  Processing completed with {warnings.length === 1 ? 'a warning' : `${warnings.length} warnings`}
                </h3>
                <div className="mt-1 text-sm text-amber-700">
                  <ul className={warnings.length > 1 ? 'list-disc pl-5 space-y-1' : ''}>
                    {warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className={`px-4 py-5 sm:p-6 flex-grow overflow-auto ${showExternalHeader ? 'pt-0' : ''}`}>
          <ErrorBoundary name="Processing Results">
            {loadingResults ? (
              <LoadingSpinner />
            ) : processingStatus === 'done' && resultsToDisplay ? (
              <ResultsContent
                resultsToDisplay={resultsToDisplay}
                editableResults={editableResults}
                isFlowArray={isFlowArray}
                hasStructuredResults={hasStructuredResults}
                editMode={editMode}
                collapsedPhases={collapsedPhases}
                openMenuPath={openMenuPath}
                setOpenMenuPath={setOpenMenuPath}
                togglePhaseCollapse={togglePhaseCollapse}
                handleInputChange={handleInputChange}
                handleDeleteItem={handleDeleteItem}
                handleAddItem={handleAddItem}
                handleChangeItemType={handleChangeItemType}
                handleCreateStepFromSelection={handleCreateStepFromSelection}
                setEditableResultsState={setEditableResultsState}
              />
            ) : processingStatus === 'error' ? (
              <ProcessingErrorMessage />
            ) : processingStatus && processingStatus !== 'not_processed' && processingStatus !== 'done' ? (
              <ProcessingInProgressMessage status={processingStatus} />
            ) : (
              <NotProcessedMessage />
            )}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

/**
 * ResultsContent - Renders the actual processing results based on their type.
 */
interface ResultsContentProps {
  resultsToDisplay: ProcessingResultData;
  editableResults: ProcessingResultData;
  isFlowArray: boolean;
  hasStructuredResults: boolean;
  editMode: boolean;
  collapsedPhases: Set<string>;
  openMenuPath: string | null;
  setOpenMenuPath: React.Dispatch<React.SetStateAction<string | null>>;
  togglePhaseCollapse: (flowIndex: number, phaseIndex: number) => void;
  handleInputChange: (
    path: (string | number)[],
    value: string,
    type?: 'flow' | 'phase' | 'step' | 'block'
  ) => void;
  handleDeleteItem: (itemPath: string) => void;
  handleAddItem: (parentPath: string, itemTypeToAdd: 'phase' | 'step' | 'block') => void;
  handleChangeItemType: (itemPath: string, newType: 'phase' | 'step') => void;
  handleCreateStepFromSelection: (selection: string, blockPath: string) => void;
  setEditableResultsState: (results: ProcessingResultData) => void;
}

const ResultsContent: React.FC<ResultsContentProps> = ({
  resultsToDisplay,
  editableResults,
  isFlowArray,
  hasStructuredResults,
  editMode,
  collapsedPhases,
  openMenuPath,
  setOpenMenuPath,
  togglePhaseCollapse,
  handleInputChange,
  handleDeleteItem,
  handleAddItem,
  handleChangeItemType,
  handleCreateStepFromSelection,
  setEditableResultsState,
}) => {
  // Flow Array rendering
  if (isFlowArray && Array.isArray(resultsToDisplay)) {
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          {(resultsToDisplay as Flow[]).map((flow, flowIndex) => (
            <FlowRenderer
              key={`flow-${flowIndex}`}
              flow={flow}
              flowIndex={flowIndex}
              editMode={editMode}
              collapsedPhases={collapsedPhases}
              openMenuPath={openMenuPath}
              setOpenMenuPath={setOpenMenuPath}
              togglePhaseCollapse={togglePhaseCollapse}
              handleInputChange={handleInputChange}
              handleDeleteItem={handleDeleteItem}
              handleAddItem={handleAddItem}
              handleChangeItemType={handleChangeItemType}
              handleCreateStepFromSelection={handleCreateStepFromSelection}
            />
          ))}
        </div>
      </div>
    );
  }

  // Structured object rendering
  if (hasStructuredResults && typeof resultsToDisplay === 'object') {
    const formattedResults = formatResultsLocal(resultsToDisplay);
    if (!formattedResults) return null;
    
    return (
      <div className="space-y-6">
        {Object.entries(formattedResults).map(([key, value]) => (
          <StructuredResultField
            key={key}
            fieldKey={key}
            value={value}
            editMode={editMode}
            editableResults={editableResults}
            setEditableResultsState={setEditableResultsState}
          />
        ))}
      </div>
    );
  }

  // Raw text/JSON rendering
  return (
    <RawResultsEditor
      resultsToDisplay={resultsToDisplay}
      editMode={editMode}
      setEditableResultsState={setEditableResultsState}
    />
  );
};

/**
 * StructuredResultField - Renders a single field from structured results.
 */
interface StructuredResultFieldProps {
  fieldKey: string;
  value: unknown;
  editMode: boolean;
  editableResults: ProcessingResultData;
  setEditableResultsState: (results: ProcessingResultData) => void;
}

const StructuredResultField: React.FC<StructuredResultFieldProps> = ({
  fieldKey,
  value,
  editMode,
  editableResults,
  setEditableResultsState,
}) => {
  const isObject = typeof value === 'object' && value !== null;

  const handleObjectChange = (newValue: string) => {
    try {
      const parsed = JSON.parse(newValue);
      const newRes = { ...(editableResults as Record<string, unknown>) };
      newRes[fieldKey] = parsed;
      setEditableResultsState(newRes);
    } catch (err) {
      console.error('Invalid JSON:', err);
    }
  };

  const handleStringChange = (newValue: string) => {
    const newRes = { ...(editableResults as Record<string, unknown>) };
    newRes[fieldKey] = newValue;
    setEditableResultsState(newRes);
  };

  return (
    <div className="bg-gray-50 p-4 rounded-md">
      <h4 className="text-md font-semibold text-gray-700 capitalize mb-2">
        {fieldKey.replace(/_/g, ' ')}
      </h4>
      <div className="text-sm text-gray-600 whitespace-pre-wrap">
        {isObject ? (
          editMode ? (
            <textarea
              value={JSON.stringify(value, null, 2)}
              onChange={(e) => handleObjectChange(e.target.value)}
              className="w-full min-h-[200px] font-mono text-xs bg-gray-100 p-3 rounded overflow-auto border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <pre className="bg-gray-100 p-3 rounded overflow-auto">
              {JSON.stringify(value, null, 2)}
            </pre>
          )
        ) : (
          editMode ? (
            <input
              type="text"
              value={String(value)}
              onChange={(e) => handleStringChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            String(value)
          )
        )}
      </div>
    </div>
  );
};

/**
 * RawResultsEditor - Editor for raw text/JSON results.
 */
interface RawResultsEditorProps {
  resultsToDisplay: ProcessingResultData;
  editMode: boolean;
  setEditableResultsState: (results: ProcessingResultData) => void;
}

const RawResultsEditor: React.FC<RawResultsEditorProps> = ({
  resultsToDisplay,
  editMode,
  setEditableResultsState,
}) => {
  const isObject = typeof resultsToDisplay === 'object' && resultsToDisplay !== null;
  const displayValue = isObject 
    ? JSON.stringify(resultsToDisplay, null, 2) 
    : String(resultsToDisplay ?? '');

  const handleChange = (newValue: string) => {
    try {
      if (isObject) {
        const parsed = JSON.parse(newValue);
        setEditableResultsState(parsed);
      } else {
        setEditableResultsState(newValue);
      }
    } catch {
      setEditableResultsState(newValue);
    }
  };

  return (
    <div className="bg-gray-50 p-4 rounded-md">
      {editMode ? (
        <textarea
          value={displayValue}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full min-h-[300px] font-mono text-sm text-gray-600 whitespace-pre-wrap overflow-auto p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      ) : (
        <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-auto">
          {displayValue}
        </pre>
      )}
    </div>
  );
};

export default ProcessingResultsPane;
